import React, { ReactElement, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import { TabListScrollButtonLeft } from './TabListScrollButtonLeft';
import { TabListScrollButtonRight } from './TabListScrollButtonRight';
import styles from './style.module.scss';
import { useCheckOverFlowTabs } from './useCheckOverFlowTabs';

interface ITabProps {
  defaultTab?: number;
  activeTab?: number;
  switchTab?: (callBack: () => void, idx: number) => void;
  tabSwitcher?: (value: number) => void;
  className?: string;
  children: ReactNode;
}

interface ITabListItem {
  title: string;
  onClick?: () => void;
  className?: string;
}

const Tabs: React.FC<ITabProps> = (props) => {
  const { defaultTab, switchTab, children, className } = props;
  const initialTabValue = defaultTab || 0;
  const [activeTab, setActiveTab] = useState<number>(initialTabValue);

  const tabSwitcher = (value: number): void => {
    if (switchTab) {
      switchTab(() => setActiveTab(value), value);
    } else {
      setActiveTab(value);
    }
  };

  const childrenElement = React.Children.map(children as ReactElement, (child: ReactElement) =>
    React.cloneElement(child, { activeTab, tabSwitcher }),
  );

  const additionalTabClass = cn('tabs', styles.tabs, className);

  return <div className={additionalTabClass}>{childrenElement}</div>;
};

const TabList: React.FC<ITabProps & { tabListItemClassName?: string }> = (props) => {
  const { tabSwitcher, activeTab, className, children, tabListItemClassName } = props;
  const tabs = React.Children.toArray(children);

  const { ref: tabListRef, handleScroll, showButton } = useCheckOverFlowTabs<HTMLUListElement>();

  const handlerClickTabList = useCallback(
    (i: number) => {
      tabSwitcher?.(i);
    },
    [tabSwitcher],
  );

  const additionalTabListClass = cn('tab-list', styles.tabList, className);

  return (
    <div className={styles.tabListWrapper}>
      <TabListScrollButtonLeft handleScroll={handleScroll} showButton={showButton} />

      <ul ref={tabListRef} className={additionalTabListClass}>
        {tabs.map((tabItem, i) => {
          return (
            <li
              key={i}
              className={cn('tab-list__tab', styles.tabList__tab, tabListItemClassName, { active: activeTab === i })}
              onClick={() => handlerClickTabList(i)}>
              {tabItem}
            </li>
          );
        })}
      </ul>

      <TabListScrollButtonRight handleScroll={handleScroll} showButton={showButton} />
    </div>
  );
};

const TabListItem: React.FC<ITabListItem> = (props) => {
  const { i18n } = useTranslation();
  const { title, onClick, className } = props;

  return (
    <span className={className} onClick={onClick}>
      {i18n.t(title as KeyMapKeys)}
    </span>
  );
};

const TabPanel: React.FC<ITabProps> = (props) => {
  const { children, activeTab, className } = props;
  const panel = React.Children.toArray(children)[activeTab as number];
  const additionalTabPanelClass = cn('tab-panel', styles.tabPanel, className);

  return <div className={additionalTabPanelClass}>{panel}</div>;
};

export { Tabs, TabList, TabListItem, TabPanel };
